import {
  Component,
  ViewChild,
  Input,
  Output,
  EventEmitter,
  inject,
  input,
  effect,
  ChangeDetectionStrategy,
} from '@angular/core'
import { ModalContainerComponent } from '../../_common/modal-container/modal-container.component'
import { BaseListing } from '../../../pages/_base-listing/base-listing'
import { CoreService } from '../../../services'
import {
  Category,
  Currency,
  Depot,
  Product,
  ProductDepotMetric,
  ProductPrice,
  Supplier,
  Tag,
  Vat,
} from 'depoto-core/src/entities'
import { GeoConfig, GeoListItemType } from '../../../configs'
import { NotificationService } from '../../../services'
import { CategoryTreeType } from '../../../models'
import { TranslateService } from '@ngx-translate/core'
import { injectQuery } from '@tanstack/angular-query-experimental'
import { CategoryService } from '../../../services/entity-services/category.service'
import { SupplierService } from '../../../services/entity-services/supplier.services'
import { VatService } from '../../../services/entity-services/vat.services'
import { PaymentService } from '../../../services/entity-services/payment.service'
import { SchemaUtil } from '../../../utils'

const optionKeepCurrent = 'keep.current'
const optionKeepCurrentSetSelected = 'keep.current.set.selected'
const optionSelectDepot = 'depot.select'
const productDepotMetricFields = ['quantityMinimum', 'quantityOptimum', 'quantityMaximum'] as const
// const optionKeepCurrent = { id: null, name: 'keep.current'}
// const optionKeepCurrentSetSelected = { id: null, name: 'keep.current.set.selected'}

class PercentageDiscount {
  percent: null
  available: 1000
  dateFrom: null
  dateTo: null
}
interface Schema {
  id: null
  name?: null
  sellPrice?: null
  tags?: {
    id: null
  }
  categories?: {
    id: null
  }
  mainCategory?: {
    id: null
  }
  vat?: {
    id: null
  }
  dimensionX?: null
  dimensionY?: null
  dimensionZ?: null
  weight?: null
  originCountry?: null
  hsCode?: null
  enabled?: null
  isPML?: null
  isAdultOnly?: null
  isFragile?: null
  isOversize?: null
  purchasePrice?: null
  purchaseCurrency?: {
    id: null
  }
  supplier?: {
    id: null
  }
}
@Component({
  selector: 'modal-bulk-edits',
  templateUrl: './modal-bulk-edits.component.html',
  styleUrls: ['./modal-bulk-edits.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ModalBulkEditsComponent extends BaseListing {
  private categoryService = inject(CategoryService)
  private supplierService = inject(SupplierService)
  private vatService = inject(VatService)
  private paymentService = inject(PaymentService)

  public categoryTreeTypeEnum = CategoryTreeType
  @ViewChild('modal') childModal: ModalContainerComponent
  @Output() refreshProductsPage: EventEmitter<any> = new EventEmitter<any>()
  @Output() setCheckedProductsToFalse: EventEmitter<any> = new EventEmitter<any>()
  @Input() filters: any = {}
  @Input() checkedProducts = []
  allProductsSelected = input<boolean>(false)

  @Input() tags: Tag[] = []
  @Input() depots: Depot[] = []
  @Input() dataCyAttribute: string

  tagSelectedOption = optionKeepCurrentSetSelected

  categorySelectedOption = optionKeepCurrentSetSelected
  mainCategorySelectedOption = optionKeepCurrent
  mainCategorySelectOptions = [optionKeepCurrent, 'set.selected', 'remove.current']

  selectOptions = ['keep.current.set.selected', 'keep.current.remove.selected', 'remove.current.add.selected']

  booleanSelectOptions = [optionKeepCurrent, 'yes', 'no']
  isFragileSelectedOption = optionKeepCurrent
  isOversizeSelectedOption = optionKeepCurrent
  isEnabledOption = optionKeepCurrent
  isAdultOnlyOption = optionKeepCurrent
  isPMLOption = optionKeepCurrent

  vats: Array<Partial<Vat> | string> = [optionKeepCurrent]
  countries: Array<Partial<GeoListItemType> | string> = [optionKeepCurrent]
  currencies: Array<Partial<Currency> | string> = [optionKeepCurrent]
  suppliers: Array<Partial<Supplier> | string> = [optionKeepCurrent]
  categories: Array<Partial<Category> | string> = [optionKeepCurrent]

  purchasePrice: any
  selectedProductDepotMetricDepot: Depot | string = optionSelectDepot
  productDepotMetricChanges: Partial<ProductDepotMetric> = this.getDefaultProductDepotMetricChanges()

  productChanges: Partial<Product> | any = {
    tags: [],
    categories: [],
    mainCategory: null,
    dimensionX: null,
    dimensionY: null,
    dimensionZ: null,
    weight: null,
    originCountry: optionKeepCurrent,
    hsCode: null,
    isOversize: null,
    isFragile: null,
    purchaseCurrency: optionKeepCurrent,
    purchasePrice: null,
    supplier: optionKeepCurrent,
    vat: optionKeepCurrent,
    enabled: null,
    isPML: null,
    isAdultOnly: null,
  }

  public advancedPrice = new ProductPrice()
  percentageDiscount = new PercentageDiscount()
  productsFetched: Partial<Product>[] | any = []

  loading = false

  numberOfAllProducts: number
  numberOfUpdatedProducts: number
  isShowingProgress = false
  isShowingCloseBtn = false
  updateErrors = []
  wasKilled = false
  updating = false
  productsWithErrors = []
  isProgressFinish = false

  constructor(
    protected core: CoreService,
    private notificationService: NotificationService,
    private translateService: TranslateService
  ) {
    super(core)
    this.countries = [optionKeepCurrent, ...GeoConfig.getCountries(this.translateService.currentLang)]
    this.translateService.onLangChange.subscribe(() => {
      this.countries = [optionKeepCurrent, ...GeoConfig.getCountries(this.translateService.currentLang)]
    })
    effect(() => {
      this.queryCategories.data()
      this.queryVats.data()
      this.querySuppliers.data()
      this.queryCurrencies.data()
    })
  }

  async showChildModal() {
    this.loading = true
    if (!this.allProductsSelected() && this.checkedProducts?.length < 1) {
      alert(this.translateService.instant('modal-bulk-edits.select-product'))
      return
    }
    if (this.vats?.length === 0 || (this.vats?.length === 1 && this.vats[0] === optionKeepCurrent))
      await this.getOptions()
    // if (!this.productChanges.vat?.length) this.setDefaultValues(true)
    this.setDefaultValues(true)
    this.loading = false
    this.childModal.showChildModal()
  }

  hideChildModal() {
    if (this.isShowingProgress && !this.isProgressFinish) {
      if (confirm(this.translateService.instant('modal-bulk-edits.terminate-action'))) {
        this.wasKilled = true
      } else return
    }
    this.setDefaultValues()

    this.setCheckedProductsToFalse.emit()
    this.refreshProductsPage.emit()
    this.childModal.hideChildModal()
  }

  get isModalShown(): boolean {
    return this.childModal?.isShown() || false
  }

  async getOptions() {
    this.categories = this.queryCategories.data()
    this.currencies = [...this.currencies, ...this.queryCurrencies.data()]
    this.suppliers = [...this.suppliers, ...this.querySuppliers.data()]
    this.vats = [...this.vats, ...this.queryVats.data()]
  }

  readonly queryCategories = injectQuery(() => ({
    queryKey: ['categories', 'options'],
    queryFn: async () => {
      return await this.categoryService
        .getCategories({ direction: 'ASC', filters: { hasParent: false } })
        .then(result => {
          return result.items
        })
    },
  }))

  readonly queryCurrencies = injectQuery(() => ({
    queryKey: ['currencies', 'options'],
    queryFn: async () => {
      return await this.paymentService.getCurrencies({}, { id: null, name: null, ratio: null }).then(result => {
        return result.items
      })
    },
  }))

  readonly querySuppliers = injectQuery(() => ({
    queryKey: ['suppliers'],
    queryFn: async () => {
      return await this.supplierService.getSuppliers({ sort: 'name', direction: 'asc' }).then(result => {
        return result.items
      })
    },
  }))

  readonly queryVats = injectQuery(() => ({
    queryKey: ['vats'],
    queryFn: async () => {
      return await this.vatService.getVats({}, { id: null, name: null, percent: null, default: null }).then(result => {
        return result.items
      })
    },
  }))

  setDefaultProductChanges() {
    this.productChanges = {
      tags: [],
      categories: [],
      mainCategory: null,
      dimensionX: null,
      dimensionY: null,
      dimensionZ: null,
      enabled: null,
      isPML: null,
      isAdultOnly: null,
      weight: null,
      originCountry: optionKeepCurrent,
      hsCode: null,
      isOversize: null,
      isFragile: null,
      purchaseCurrency: optionKeepCurrent,
      purchasePrice: null,
      supplier: optionKeepCurrent,
      vat: optionKeepCurrent,
    }
  }

  get productDepotMetricDepotOptions(): Array<Depot | string> {
    return [optionSelectDepot, ...(this.depots ?? [])]
  }

  getDefaultProductDepotMetricChanges(): Partial<ProductDepotMetric> {
    return {
      quantityMinimum: null,
      quantityOptimum: null,
      quantityMaximum: null,
    }
  }

  close() {
    if (this.isShowingProgress && !this.isProgressFinish) {
      if (confirm(this.translateService.instant('modal-bulk-edits.terminate-action'))) {
        this.wasKilled = true
      } else return
    }
    this.setDefaultValues()
    this.hideChildModal()
  }

  setDefaultValues(isShowingModal = false): void {
    this.setDefaultProductChanges()
    this.selectedProductDepotMetricDepot = optionSelectDepot
    this.productDepotMetricChanges = this.getDefaultProductDepotMetricChanges()
    this.advancedPrice = new ProductPrice()
    this.tagSelectedOption = optionKeepCurrentSetSelected
    this.categorySelectedOption = optionKeepCurrentSetSelected
    this.mainCategorySelectedOption = optionKeepCurrent
    this.isEnabledOption = optionKeepCurrent
    this.isPMLOption = optionKeepCurrent
    this.isAdultOnlyOption = optionKeepCurrent
    this.isFragileSelectedOption = optionKeepCurrent
    this.isOversizeSelectedOption = optionKeepCurrent
    this.productsFetched = []
    this.numberOfAllProducts = undefined
    this.numberOfUpdatedProducts = undefined
    this.updateErrors = []
    this.productsWithErrors = []

    this.isShowingProgress = false
    this.isShowingCloseBtn = false
    this.updating = false
    this.isProgressFinish = false
    this.wasKilled = false
    if (!isShowingModal) {
      this.refreshProductsPage.emit()
      this.setCheckedProductsToFalse.emit()
    }
  }

  suspend() {
    this.wasKilled = true
  }

  getTagValue(tag): boolean {
    return this.productChanges.tags.some(t => t.id === tag.id)
  }

  updateTags(tag: Tag, value: boolean) {
    if (value) {
      this.productChanges.tags.push(tag)
    } else {
      this.productChanges.tags.forEach((t: Tag, i: number) => {
        if (t.id === tag.id) {
          this.productChanges.tags.splice(i, 1)
        }
      })
    }
  }

  booleanChange(event, property: string) {
    if (property === 'isFragile') this.isFragileSelectedOption = event
    else if (property === 'isOversize') this.isOversizeSelectedOption = event
    else if (property === 'enabled') this.isEnabledOption = event
    else if (property === 'isMPL') this.isPMLOption = event
    else if (property === 'isAdultOnly') this.isAdultOnlyOption = event
  }

  valueAdded(event) {
    if (event[1] === 'originCountry') this.productChanges.originCountry = event[0]
    else if (event[1] === 'weight') this.productChanges.weight = event[0]
    else if (event[1] === 'dimensionX') this.productChanges.dimensionX = event[0]
    else if (event[1] === 'dimensionY') this.productChanges.dimensionY = event[0]
    else if (event[1] === 'dimensionZ') this.productChanges.dimensionZ = event[0]
    else if (event[1] === 'hsCode') this.productChanges.hsCode = event[0]
  }

  mainCategoryChanged(event) {
    this.productChanges.mainCategory = event.id
  }

  async updateProduct(product, changedProperties, schemaForUpdate, metricChanges: Partial<ProductDepotMetric>) {
    if (this.wasKilled) {
      this.setDefaultValues()
      this.hideChildModal()
      return
    }

    const productName = product.name
    product = { id: product.id, sellPrice: product.sellPrice, ...changedProperties }
    const shouldUpdateProduct = this.shouldUpdateProduct(changedProperties)

    if (shouldUpdateProduct && this.productChanges.tags?.length) {
      await this.fetchTagsOnProduct(product)
      this.setTagsOnProductToUpdate(product)
    }

    if (shouldUpdateProduct && this.productChanges.categories?.length) {
      await this.fetchCategoriesOnProduct(product)
      this.setCategoriesOnProductToUpdate(product)
    }

    if (shouldUpdateProduct && this.percentageDiscount.percent) {
      await this.createAdvancedPriceFromPercent(product)
    }

    try {
      if (shouldUpdateProduct) {
        await this.update(product, schemaForUpdate)
        if (this.advancedPrice.sellPrice) {
          this.advancedPrice.product = product
          await this.core.services.productPrice.create(this.advancedPrice)
        }
        if (this.percentageDiscount.percent) {
          this.createAdvancedPriceFromPercent(product)
        }
      }
      if (this.hasProductDepotMetricChanges(metricChanges)) {
        await this.saveProductDepotMetric(product.id, metricChanges)
      }
    } catch (err) {
      this.updateErrors.push({
        productId: product.id,
        productName: productName,
        errMessage: this.getErrorMessage(err),
      })
    } finally {
      this.numberOfUpdatedProducts = this.numberOfUpdatedProducts + 1
      if (shouldUpdateProduct) {
        await this.core.services.product.getById(product.id, schemaForUpdate)
      }
    }
  }

  async createAdvancedPriceFromPercent(product) {
    const advancedPrice = new ProductPrice({
      available: this.percentageDiscount.available,
      product: product,
      dateFrom: this.percentageDiscount.dateFrom,
      dateTo: this.percentageDiscount.dateTo,
    })
    advancedPrice.sellPrice = product.sellPrice * (1 - Number(this.percentageDiscount.percent) / 100)
    await this.core.services.productPrice.create(advancedPrice)
  }

  async save() {
    this.loading = true
    this.numberOfUpdatedProducts = 0
    const metricChanges = this.getProductDepotMetricChangesForUpdate()
    if (!this.validateProductDepotMetricChanges(metricChanges)) {
      this.loading = false
      return
    }
    let page = 1
    let endPage: number
    const schema: Schema = { id: null, name: null }
    const schemaForUpdate: Schema = { id: null }
    if (this.productChanges.tags?.length) schema.tags = { id: null }
    if (this.productChanges.categories?.length) schema.categories = { id: null }
    if (this.percentageDiscount) schema.sellPrice = null

    if (this.isEnabledOption !== optionKeepCurrent)
      this.isEnabledOption === 'yes' ? (this.productChanges.enabled = true) : (this.productChanges.enabled = false)
    if (this.isPMLOption !== optionKeepCurrent)
      this.isPMLOption === 'yes' ? (this.productChanges.isPML = true) : (this.productChanges.isPML = false)
    if (this.isAdultOnlyOption !== optionKeepCurrent)
      this.isAdultOnlyOption === 'yes'
        ? (this.productChanges.isAdultOnly = true)
        : (this.productChanges.isAdultOnly = false)
    if (this.isFragileSelectedOption !== optionKeepCurrent)
      this.isFragileSelectedOption === 'yes'
        ? (this.productChanges.isFragile = true)
        : (this.productChanges.isFragile = false)
    if (this.isOversizeSelectedOption !== optionKeepCurrent)
      this.isOversizeSelectedOption === 'yes'
        ? (this.productChanges.isOversize = true)
        : (this.productChanges.isOversize = false)

    //object with changed properties from productChanges
    const changedProperties = {}
    if (this.mainCategorySelectedOption === 'remove.current') {
      this.productChanges.mainCategory = null
      changedProperties['mainCategory'] = null
      schemaForUpdate['mainCategory'] = { id: null }
    }
    const entries = Object.entries(this.productChanges)
    for (const [key, value] of entries) {
      if (value !== 'keep.current' && value !== null && key !== 'tags' && key !== 'categories') {
        if (['vat', 'purchaseCurrency', 'mainCategory'].includes(key)) {
          schemaForUpdate[key] = { id: null }
          changedProperties[key] = { id: value }
        } else if (['supplier'].includes(key)) {
          schemaForUpdate[key] = { id: null }
          changedProperties[key] = value
        } else {
          changedProperties[key] = value
        }
      }
    }

    //for avoid error alerts while update
    this.notificationService.avoidErrorAlerts = true

    if (this.allProductsSelected()) {
      // Phase 1: Snapshot all matching product IDs (and sellPrice if needed) with stable sort by id ASC
      const snapshot: Array<{ id: number; name?: string; sellPrice?: number }> = []
      const snapshotSchema: any = { id: null, name: null }
      if (this.percentageDiscount?.percent != null) {
        snapshotSchema.sellPrice = null
      }
      do {
        if (this.wasKilled) {
          this.setDefaultValues()
          this.hideChildModal()
          return
        }
        const { items, paginator } = await this.core.services.product.getList(
          { page: page, sort: 'id', direction: 'asc', filters: this.filters },
          snapshotSchema
        )
        if (page === 1) {
          this.numberOfAllProducts = paginator.totalCount
          endPage = paginator.endPage
          this.loading = false
          this.isShowingProgress = true
          this.updating = true
        }
        // collect snapshot of ids (+sellPrice if requested)
        for (const p of items as any[]) {
          snapshot.push({ id: (p as any).id, name: (p as any).name, sellPrice: (p as any).sellPrice })
        }
        page++
      } while (page <= endPage)

      // Phase 2: Update by frozen snapshot in batches to limit concurrency
      const batchSize = 25
      for (let i = 0; i < snapshot.length; i += batchSize) {
        if (this.wasKilled) {
          this.setDefaultValues()
          this.hideChildModal()
          return
        }
        const batch = snapshot.slice(i, i + batchSize)
        await Promise.all(
          batch.map(product => this.updateProduct(product, changedProperties, schemaForUpdate, metricChanges))
        )
      }
    } else {
      this.numberOfAllProducts = this.checkedProducts?.length
      this.loading = false
      this.isShowingProgress = true
      this.updating = true
      await Promise.all(
        this.checkedProducts.map(product =>
          this.updateProduct(product, changedProperties, schemaForUpdate, metricChanges)
        )
      )
    }

    this.isProgressFinish = true
    //allow err alerts
    this.notificationService.avoidErrorAlerts = false

    // hide suspend button, show close btn
    this.updating = false
    this.isShowingCloseBtn = true
  }

  async update(product, schema) {
    await this.core.services.product.updateProductPart(product, schema)
  }

  private shouldUpdateProduct(changedProperties): boolean {
    return (
      Object.keys(changedProperties).length > 0 ||
      this.productChanges.tags?.length > 0 ||
      this.productChanges.categories?.length > 0 ||
      !!this.advancedPrice.sellPrice ||
      !!this.percentageDiscount.percent
    )
  }

  private hasProductDepotMetricChanges(metricChanges: Partial<ProductDepotMetric>): boolean {
    return productDepotMetricFields.some(field => metricChanges[field] !== undefined)
  }

  private getProductDepotMetricChangesForUpdate(): Partial<ProductDepotMetric> {
    const changes: Partial<ProductDepotMetric> = {}
    productDepotMetricFields.forEach(field => {
      const value = this.productDepotMetricChanges[field]
      if (this.isPositiveMetricValue(value)) {
        changes[field] = Number(value)
      }
    })
    return changes
  }

  private validateProductDepotMetricChanges(metricChanges: Partial<ProductDepotMetric>): boolean {
    if (productDepotMetricFields.some(field => this.isNegativeMetricValue(this.productDepotMetricChanges[field]))) {
      alert(this.translateService.instant('modal-bulk-edits.metrics.negative-values'))
      return false
    }
    if (this.hasProductDepotMetricChanges(metricChanges) && !this.getSelectedProductDepotMetricDepotId()) {
      alert(this.translateService.instant('modal-bulk-edits.metrics.select-depot'))
      return false
    }
    return true
  }

  private isPositiveMetricValue(value): boolean {
    const numericValue = Number(value)
    return value !== null && value !== undefined && value !== '' && !isNaN(numericValue) && numericValue > 0
  }

  private isNegativeMetricValue(value): boolean {
    const numericValue = Number(value)
    return value !== null && value !== undefined && value !== '' && !isNaN(numericValue) && numericValue < 0
  }

  private getSelectedProductDepotMetricDepotId(): number {
    if (
      this.selectedProductDepotMetricDepot &&
      typeof this.selectedProductDepotMetricDepot === 'object' &&
      'id' in this.selectedProductDepotMetricDepot
    ) {
      return Number(this.selectedProductDepotMetricDepot.id)
    }
    return 0
  }

  private async saveProductDepotMetric(productId: number, metricChanges: Partial<ProductDepotMetric>) {
    const depotId = this.getSelectedProductDepotMetricDepotId()
    const result = await this.core.services.productDepotMetric.getList(
      { page: 1, sort: 'id', direction: null, filters: { product: productId } },
      SchemaUtil.productDepotMetric
    )
    const productDepotMetric = result.items?.find(metric => Number(metric.depot?.id) === depotId)
    if (productDepotMetric?.id) {
      await this.core.services.productDepotMetric.update(
        { id: productDepotMetric.id, ...metricChanges },
        SchemaUtil.productDepotMetric
      )
    } else {
      await this.core.services.productDepotMetric.create(
        {
          depot: { id: depotId },
          product: { id: productId },
          ...metricChanges,
        },
        SchemaUtil.productDepotMetric
      )
    }
  }

  private getErrorMessage(err) {
    let errMessage
    if (Object.prototype.toString.call(err?.[0]) === '[object Array]') {
      for (const object of err) {
        for (const key in object) {
          if (object.hasOwnProperty(key)) {
            errMessage = object[key]
          }
        }
      }
    } else errMessage = err?.[0] || err?.message || err
    return errMessage
  }

  setTagsOnProductToUpdate(product) {
    if (this.tagSelectedOption === 'keep.current.set.selected') {
      this.productChanges.tags.forEach(tag => {
        if (!product.tags?.find(t => t.id === tag.id)) product.tags.push(tag)
      })
    } else if (this.tagSelectedOption === 'keep.current.remove.selected') {
      for (const changedTag of this.productChanges.tags) {
        if (product.tags.some(tag => tag.id === changedTag.id))
          product.tags = product.tags.filter(tag => tag.id !== changedTag.id)
      }
    } else if (this.tagSelectedOption === 'remove.current.add.selected') {
      product.tags = this.productChanges.tags
    }
    // TODO check jestli nejsou arays puvodni a na upravu stejne => neposilat tags
  }

  async fetchCategoriesOnProduct(product) {
    const productWithCategories = await this.core.services.product.getById(product.id, {
      categories: {
        id: null,
        externalId: null,
        name: null,
      },
    })
    product.categories = productWithCategories.categories
  }

  async fetchTagsOnProduct(product) {
    const productWithTags = await this.core.services.product.getById(product.id, {
      tags: {
        id: null,
        name: null,
      },
    })
    product.tags = productWithTags.tags
  }

  setCategoriesOnProductToUpdate(product) {
    if (this.categorySelectedOption === 'keep.current.set.selected') {
      this.productChanges.categories.forEach(cat => {
        if (!product.categories?.find(c => c.id === cat.id)) product.categories.push(cat)
      })
    } else if (this.categorySelectedOption === 'keep.current.remove.selected') {
      for (const changedCategory of this.productChanges.categories) {
        if (product.categories.some(cat => cat.id === changedCategory.id))
          product.categories = product.categories.filter(category => category.id !== changedCategory.id)
      }
    } else if (this.categorySelectedOption === 'remove.current.add.selected') {
      product.categories = this.productChanges.categories
    }
    // TODO check jestli nejsou arays puvodni a na upravu stejne => neposilat categories
  }

  filter(pageNo: number, sortProp: string, sortDir: string, concat: boolean): void {}
}
